import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Autocomplete,
  Chip,
  Card,
  CardContent,
  Divider,
  Alert,
  LinearProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Psychology as AIIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const PerformanceReviewForm = ({ employeeId, onSuccess }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    employeeId: employeeId || '',
    reviewPeriod: 'MONTHLY',
    reviewStartDate: '',
    reviewEndDate: '',
    reviewDate: new Date().toISOString().split('T')[0],
    kpiEvaluations: [],
    strengths: [''],
    areasOfImprovement: [''],
    achievements: [''],
    previousGoals: [],
    newGoals: [{ goal: '', targetDate: '', kpiId: '' }],
    pipRequired: false,
    pipDetails: {
      startDate: '',
      endDate: '',
      objectives: [''],
      supportProvided: [''],
      reviewFrequency: 'BI_WEEKLY'
    },
    recommendedForPromotion: false,
    recommendedForIncrement: false,
    recommendedIncrementPercentage: 0,
    recommendedForTraining: false,
    trainingRecommendations: [''],
    reviewerComments: ''
  });

  const [employees, setEmployees] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [selectedKPIs, setSelectedKPIs] = useState([]);
  const [calculatedScores, setCalculatedScores] = useState({
    totalScore: 0,
    maxScore: 100,
    achievementPercentage: 0,
    rating: 'MEETS_EXPECTATIONS'
  });
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  const userMeta = localStorage.getItem('login_meta');
  const { companyCode, branchId } = userMeta ? JSON.parse(userMeta) : {};

  // Fetch employees and KPIs
  useEffect(() => {
    fetchEmployees();
    fetchKPIs();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/employee/getAllEmployees');
      if (response.data) {
        setEmployees(response.data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchKPIs = async () => {
    try {
      const response = await axios.post('/api/employee-performance/kpi/list', { 
        isActive: true,
        page: 1,
        limit: 100
      });
      if (response.data.success) {
        setKpis(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    }
  };

  // Handle KPI selection
  const handleKPISelect = (kpi) => {
    if (!selectedKPIs.find(k => k._id === kpi._id)) {
      const newEvaluation = {
        kpiId: kpi._id,
        kpiName: kpi.kpiName,
        targetValue: kpi.targetValue || 0,
        achievedValue: 0,
        achievementPercentage: 0,
        weightage: kpi.weightage || 10,
        score: 0,
        comments: ''
      };
      
      setSelectedKPIs([...selectedKPIs, kpi]);
      setFormData(prev => ({
        ...prev,
        kpiEvaluations: [...prev.kpiEvaluations, newEvaluation]
      }));
    }
  };

  // Remove KPI
  const handleRemoveKPI = (index) => {
    setSelectedKPIs(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      kpiEvaluations: prev.kpiEvaluations.filter((_, i) => i !== index)
    }));
  };

  // Update KPI evaluation
  const handleKPIEvaluationChange = (index, field, value) => {
    const newEvaluations = [...formData.kpiEvaluations];
    newEvaluations[index][field] = value;

    // Calculate achievement percentage and score
    if (field === 'achievedValue' || field === 'targetValue') {
      const targetValue = parseFloat(newEvaluations[index].targetValue) || 0;
      const achievedValue = parseFloat(newEvaluations[index].achievedValue) || 0;
      const achievementPercentage = targetValue > 0 ? (achievedValue / targetValue) * 100 : 0;
      
      newEvaluations[index].achievementPercentage = Math.round(achievementPercentage * 100) / 100;
      newEvaluations[index].score = Math.round((achievementPercentage / 100) * newEvaluations[index].weightage * 100) / 100;
    }

    setFormData(prev => ({ ...prev, kpiEvaluations: newEvaluations }));
    calculateOverallScores(newEvaluations);
  };

  // Calculate overall scores
  const calculateOverallScores = (evaluations) => {
    const totalScore = evaluations.reduce((sum, evaluation) => sum + (evaluation.score || 0), 0);
    const maxScore = evaluations.reduce((sum, evaluation) => sum + (evaluation.weightage || 0), 0);
    const achievementPercentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;

    let rating = 'MEETS_EXPECTATIONS';
    if (achievementPercentage >= 90) rating = 'OUTSTANDING';
    else if (achievementPercentage >= 75) rating = 'EXCEEDS_EXPECTATIONS';
    else if (achievementPercentage >= 60) rating = 'MEETS_EXPECTATIONS';
    else if (achievementPercentage >= 40) rating = 'NEEDS_IMPROVEMENT';
    else rating = 'UNSATISFACTORY';

    setCalculatedScores({
      totalScore: Math.round(totalScore * 100) / 100,
      maxScore,
      achievementPercentage: Math.round(achievementPercentage * 100) / 100,
      rating
    });
  };

  // Handle array field changes
  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const handleAddArrayField = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const handleRemoveArrayField = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Submit form
  const handleSubmit = async () => {
    // Validation
    if (!formData.employeeId) {
      Swal.fire('Error', 'Please select an employee', 'error');
      return;
    }

    if (formData.kpiEvaluations.length === 0) {
      Swal.fire('Error', 'Please add at least one KPI evaluation', 'error');
      return;
    }

    if (!formData.reviewStartDate || !formData.reviewEndDate) {
      Swal.fire('Error', 'Please select review period dates', 'error');
      return;
    }

    setLoading(true);

    try {
      // Clean up empty array values
      const cleanedData = {
        ...formData,
        strengths: formData.strengths.filter(s => s.trim()),
        areasOfImprovement: formData.areasOfImprovement.filter(a => a.trim()),
        achievements: formData.achievements.filter(a => a.trim()),
        trainingRecommendations: formData.trainingRecommendations.filter(t => t.trim()),
        newGoals: formData.newGoals.filter(g => g.goal.trim())
      };

      const response = await axios.post('/api/employee-performance/review/create', cleanedData);

      if (response.data.success) {
        setAiInsights(response.data.data.aiInsights);
        
        Swal.fire({
          title: '✅ Performance Review Created!',
          icon: 'success',
          html: `
            <div style="text-align: left; margin-top: 20px; padding: 0 20px;">
              <div style="background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 15px;">
                <h3 style="margin: 0 0 10px 0; color: #1976d2;">Review Summary</h3>
                <p style="margin: 5px 0;"><strong>Overall Rating:</strong> <span style="color: #4caf50; font-size: 18px;">${response.data.data.rating.replace(/_/g, ' ')}</span></p>
                <p style="margin: 5px 0;"><strong>Total Score:</strong> ${response.data.data.totalScore} / ${response.data.data.maxScore}</p>
                <p style="margin: 5px 0;"><strong>Achievement:</strong> <span style="color: #2196f3; font-size: 18px; font-weight: bold;">${response.data.data.achievementPercentage}%</span></p>
              </div>
              ${response.data.data.aiInsights ? `
                <div style="background: #e3f2fd; padding: 15px; border-radius: 8px; border-left: 4px solid #2196f3;">
                  <h3 style="margin: 0 0 10px 0; color: #1976d2;">🤖 AI-Powered Insights</h3>
                  <p style="margin: 5px 0;"><strong>Performance Trend:</strong> ${response.data.data.aiInsights.performanceTrend}</p>
                  <p style="margin: 5px 0;"><strong>Risk Level:</strong> <span style="color: ${response.data.data.aiInsights.riskLevel === 'HIGH' ? '#f44336' : response.data.data.aiInsights.riskLevel === 'MEDIUM' ? '#ff9800' : '#4caf50'};">${response.data.data.aiInsights.riskLevel}</span></p>
                  <p style="margin: 10px 0 5px 0;"><strong>Recommendations:</strong></p>
                  <ul style="text-align: left; padding-left: 20px; margin: 5px 0;">
                    ${response.data.data.aiInsights.recommendations.map(r => `<li style="margin: 5px 0;">${r}</li>`).join('')}
                  </ul>
                </div>
              ` : ''}
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: '📋 View All Reviews',
          cancelButtonText: 'Close',
          confirmButtonColor: '#1976d2',
          cancelButtonColor: '#757575',
          width: '600px'
        }).then((result) => {
          if (result.isConfirmed) {
            navigate('/userdashboard/staff/hrm/performance/review/list');
          }
        });

        if (onSuccess) onSuccess(response.data.data);
      }
    } catch (error) {
      console.error('Error creating review:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to create review', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Get rating color
  const getRatingColor = (rating) => {
    const colors = {
      OUTSTANDING: '#4caf50',
      EXCEEDS_EXPECTATIONS: '#8bc34a',
      MEETS_EXPECTATIONS: '#2196f3',
      NEEDS_IMPROVEMENT: '#ff9800',
      UNSATISFACTORY: '#f44336'
    };
    return colors[rating] || '#757575';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Create Performance Review
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Basic Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Basic Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={employees.filter(e => e._id)} 
              getOptionLabel={(option) => `${option.staff_name || option.name || 'Unknown'} (${option.employeeCode || option.emp_id || 'N/A'})`}
              value={employees.find(e => e._id === formData.employeeId) || null}
              onChange={(e, value) => setFormData(prev => ({ ...prev, employeeId: value?._id || '' }))}
              renderInput={(params) => (
                <TextField {...params} label="Employee *" required />
              )}
              disabled={!!employeeId}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Review Period *"
              value={formData.reviewPeriod}
              onChange={(e) => setFormData(prev => ({ ...prev, reviewPeriod: e.target.value }))}
            >
              <MenuItem value="PROBATION">Probation</MenuItem>
              <MenuItem value="MONTHLY">Monthly</MenuItem>
              <MenuItem value="QUARTERLY">Quarterly</MenuItem>
              <MenuItem value="HALF_YEARLY">Half Yearly</MenuItem>
              <MenuItem value="YEARLY">Yearly</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Review Start Date *"
              value={formData.reviewStartDate}
              onChange={(e) => setFormData(prev => ({ ...prev, reviewStartDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Review End Date *"
              value={formData.reviewEndDate}
              onChange={(e) => setFormData(prev => ({ ...prev, reviewEndDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Review Date"
              value={formData.reviewDate}
              onChange={(e) => setFormData(prev => ({ ...prev, reviewDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* KPI Evaluations */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            KPI Evaluations
          </Typography>
          <Autocomplete
            options={kpis}
            getOptionLabel={(option) => `${option.kpiName} (${option.category})`}
            onChange={(e, value) => value && handleKPISelect(value)}
            renderInput={(params) => (
              <TextField {...params} label="Add KPI" sx={{ width: 300 }} />
            )}
            value={null}
          />
        </Box>

        {formData.kpiEvaluations.length === 0 ? (
          <Alert severity="info">No KPIs added yet. Please select KPIs to evaluate.</Alert>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>KPI</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Achieved</TableCell>
                <TableCell>Achievement %</TableCell>
                <TableCell>Weightage</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Comments</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.kpiEvaluations.map((evaluation, index) => (
                <TableRow key={index}>
                  <TableCell>{evaluation.kpiName}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={evaluation.targetValue}
                      onChange={(e) => handleKPIEvaluationChange(index, 'targetValue', e.target.value)}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={evaluation.achievedValue}
                      onChange={(e) => handleKPIEvaluationChange(index, 'achievedValue', e.target.value)}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${evaluation.achievementPercentage}%`}
                      color={evaluation.achievementPercentage >= 90 ? 'success' : 
                             evaluation.achievementPercentage >= 70 ? 'primary' : 
                             evaluation.achievementPercentage >= 50 ? 'warning' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{evaluation.weightage}</TableCell>
                  <TableCell>
                    <strong>{evaluation.score}</strong>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      multiline
                      value={evaluation.comments}
                      onChange={(e) => handleKPIEvaluationChange(index, 'comments', e.target.value)}
                      sx={{ width: 200 }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveKPI(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Overall Scores */}
        {formData.kpiEvaluations.length > 0 && (
          <Card sx={{ mt: 3, bgcolor: getRatingColor(calculatedScores.rating), color: 'white' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2">Total Score</Typography>
                  <Typography variant="h4">{calculatedScores.totalScore}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2">Max Score</Typography>
                  <Typography variant="h4">{calculatedScores.maxScore}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2">Achievement</Typography>
                  <Typography variant="h4">{calculatedScores.achievementPercentage}%</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2">Rating</Typography>
                  <Typography variant="h6">{calculatedScores.rating.replace(/_/g, ' ')}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Paper>

      {/* Qualitative Assessment */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Qualitative Assessment
        </Typography>
        
        {/* Strengths */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Strengths
          </Typography>
          {formData.strengths.map((strength, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={strength}
                onChange={(e) => handleArrayFieldChange('strengths', index, e.target.value)}
                placeholder="Enter strength..."
              />
              {formData.strengths.length > 1 && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveArrayField('strengths', index)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => handleAddArrayField('strengths')}
            size="small"
          >
            Add Strength
          </Button>
        </Box>

        {/* Areas of Improvement */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Areas of Improvement
          </Typography>
          {formData.areasOfImprovement.map((area, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={area}
                onChange={(e) => handleArrayFieldChange('areasOfImprovement', index, e.target.value)}
                placeholder="Enter area of improvement..."
              />
              {formData.areasOfImprovement.length > 1 && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveArrayField('areasOfImprovement', index)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => handleAddArrayField('areasOfImprovement')}
            size="small"
          >
            Add Area
          </Button>
        </Box>

        {/* Achievements */}
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Key Achievements
          </Typography>
          {formData.achievements.map((achievement, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={achievement}
                onChange={(e) => handleArrayFieldChange('achievements', index, e.target.value)}
                placeholder="Enter achievement..."
              />
              {formData.achievements.length > 1 && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveArrayField('achievements', index)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => handleAddArrayField('achievements')}
            size="small"
          >
            Add Achievement
          </Button>
        </Box>
      </Paper>

      {/* Reviewer Comments */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Reviewer Comments
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={formData.reviewerComments}
          onChange={(e) => setFormData(prev => ({ ...prev, reviewerComments: e.target.value }))}
          placeholder="Enter your overall comments about the employee's performance..."
        />
      </Paper>

      {/* Submit Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => window.history.back()}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          Create Review with AI Analysis
        </Button>
      </Box>
    </Box>
  );
};

export default PerformanceReviewForm;
